import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';

import SearchDebounce from './SearchDebounce';
import CloseIcon from '@mui/icons-material/Close';
import { IconButton } from '@mui/material';
import { styled } from '@mui/material/styles';
import TextField from '@mui/material/TextField';

const SearchFieldStyled = styled(TextField)(({ theme }) => ({
  '& label': {
    color: theme?.palette?.form?.labelColor || theme.palette.text.primary || '#707070',
    fontSize: theme?.typography?.pxToRem?.(14) || '0.875rem',
    paddingLeft: 16,
    '&.MuiFormLabel-filled, &.MuiInputLabel-shrink': {
      color: theme?.palette?.form?.labelFilledColor || theme.palette.text.primary || '#707070',
      fontSize: theme?.typography?.pxToRem?.(13) || '0.8125rem',
      paddingLeft: 0,
    },
    '&.Mui-focused': {
      color: theme?.palette?.primary?.main || '#1976d2',
      fontSize: theme?.typography?.pxToRem?.(13) || '0.8125rem',
      paddingLeft: 0,
    },
    '&.Mui-error': {
      color: theme?.palette?.error?.main || '#d32f2f',
      paddingLeft: 16,
    },
  },
  '& .MuiInput-root': {
    padding: '6px 12px 6px 16px',
    fontSize: theme?.typography?.pxToRem?.(14) || '0.875rem',
    '& .MuiInputBase-input': {
      fontSize: theme?.typography?.pxToRem?.(14) || '0.875rem',
      padding: 0,
    },
  },
  '& .MuiInput-underline:hover:not(.Mui-disabled):before': {
    borderBottomColor: theme?.palette?.form?.border || '#ccc',
  },
  '& .MuiInput-underline:before': {
    borderBottomColor: theme?.palette?.form?.border || '#ccc',
  },
  '& .MuiFormHelperText-root': {
    fontSize: theme?.typography?.pxToRem?.(12) || '0.75rem',
    marginLeft: 16,
  },
  '& .MuiInputAdornment-root': {
    color: theme?.palette?.form?.labelFilledColor || theme.palette.text.primary || '#707070',
  },
  '& .clearButton': {
    display: 'none',
    order: 1,
    right: 5,
    padding: 0,
    position: 'absolute',
  },
  '&:hover .clearButton': {
    display: 'inline-block',
  },
}));

const SearchField = ({
  field: { name, value, onChange, onBlur },
  form: { errors, touched },
  label,
  required,
  style,
  variant,
  fullWidth,
  disabled,
  inputProps,
  asyncErrors,
  allowClear,
  ...props
}) => {
  // State and setter for search field
  const [searchValue, setSearchValue] = useState(value || '');
  const [search, setSearch] = useState(null);

  const debouncedSearch = SearchDebounce(search, 500);

  useEffect(
    () => {
      // Make sure we have a value (user has entered something in input)
      if (debouncedSearch !== null) {
        onChange(name, debouncedSearch);
      }
    },
    // The useEffect function will only execute if this value changes ...
    // value (search) hasn't changed for more than 500ms.

    // eslint-disable-next-line
    [debouncedSearch]
  );

  useEffect(
    () => {
      // Make sure we have a value (user has entered something in input)
      setSearchValue(value);
    },
    // The useEffect function will only execute if this value changes ...
    // value (search) hasn't changed for more than 500ms.

    [value]
  );

  const handleShowError = () => {
    if (asyncErrors) {
      return asyncErrors;
    } else if (errors[name] && touched[name] && errors[name]) {
      return errors[name];
    }
  };

  const handleError = () => {
    if (asyncErrors) {
      return true;
    } else if (errors && errors[name] && touched && touched[name]) {
      return true;
    }
  };

  const onClear = () => {
    setSearch('');
    setSearchValue('');
  };

  const getInputProps = () => {
    const data = allowClear
      ? {
          endAdornment: (
            <IconButton className="clearButton" onClick={onClear}>
              <CloseIcon fontSize="small" />
            </IconButton>
          ),
          ...inputProps,
        }
      : {};
    return {
      ...data,
      ...inputProps,
    };
  };

  return (
    <SearchFieldStyled
      name={name}
      style={style}
      type="search"
      label={label}
      margin="normal"
      onBlur={onBlur}
      variant={variant}
      value={searchValue}
      required={required}
      disabled={disabled}
      fullWidth={fullWidth}
      error={handleError()}
      InputProps={{ id: name, ...getInputProps() }}
      InputLabelProps={{ htmlFor: name }}
      helperText={handleShowError()}
      onChange={(e) => {
        setSearch(e.target.value);
        setSearchValue(e.target.value);
      }}
      {...props}
    />
  );
};

SearchField.propTypes = {
  type: PropTypes.string,
  required: PropTypes.bool,
  disabled: PropTypes.bool,
  fullWidth: PropTypes.bool,
  allowClear: PropTypes.bool,
  asyncErrors: PropTypes.object,
  field: PropTypes.shape({
    value: PropTypes.any,
    name: PropTypes.string,
    onBlur: PropTypes.func,
    onChange: PropTypes.func,
  }),
  form: PropTypes.shape({
    errors: PropTypes.object,
    touched: PropTypes.object,
  }),
  inputProps: PropTypes.object,
  style: PropTypes.object,
  label: PropTypes.string.isRequired,
  variant: PropTypes.oneOf(['standard', 'outlined', 'filled']),
};

SearchField.defaultProps = {
  type: 'text',
  inputProps: {},
  field: {},
  form: {
    errors: {},
    touched: {},
  },
  required: false,
  style: {},
  fullWidth: true,
  disabled: false,
  label: 'Search...',
  variant: 'standard',
};

export default SearchField;
